import { Tooltip, Typography } from 'antd';
import type { ReactNode } from 'react';

import { BrandColors } from '../../theme/brand';

/** Một thanh trong biểu đồ. */
export interface BarDatum {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** Màu riêng của thanh; mặc định là màu nhận diện. Chỉ đặt khi màu mang nghĩa (trạng thái). */
  readonly color?: string;
  /** Biểu tượng đứng trước nhãn — bắt buộc khi thanh dùng màu trạng thái, để màu không đứng một mình. */
  readonly icon?: ReactNode;
  /** Chữ phụ sau con số, ví dụ giá trị tiền của một giai đoạn. */
  readonly extra?: string;
  /** Nội dung khi rê chuột. */
  readonly tooltip?: ReactNode;
}

/**
 * Biểu đồ thanh ngang tự vẽ — không kéo cả một thư viện biểu đồ vào chỉ để vẽ vài thanh.
 *
 * Theo quy cách trực quan hóa: thanh mảnh (14px, dưới trần 24px), đầu dữ liệu bo 4px còn gốc
 * vuông, mọi thanh mọc từ cùng một đường gốc; con số đứng ngay đầu thanh bằng màu chữ chứ không
 * bằng màu thanh. Một chuỗi dữ liệu thì một màu và không cần chú giải — tiêu đề thẻ đã nói đang vẽ
 * gì. Nhãn và con số của mọi thanh đều là chữ thật, nên biểu đồ tự là một bảng đọc được.
 */
export function BarList({
  items,
  onSelect,
}: {
  readonly items: readonly BarDatum[];
  /** Bấm một thanh — thường là mở danh sách đã lọc theo đúng nhóm đó. */
  readonly onSelect?: (key: string) => void;
}): ReactNode {
  const maximum = Math.max(1, ...items.map((item) => item.value));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} role="list">
      {items.map((item) => {
        const ratio = item.value / maximum;

        const row = (
          <div
            role="listitem"
            className={onSelect === undefined ? 'erp-bar-row' : 'erp-bar-row erp-bar-row--clickable'}
            onClick={onSelect === undefined ? undefined : () => { onSelect(item.key); }}
            onKeyDown={
              onSelect === undefined
                ? undefined
                : (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(item.key);
                    }
                  }
            }
            tabIndex={onSelect === undefined ? undefined : 0}
            aria-label={`${item.label}: ${item.value.toLocaleString('vi-VN')}`}
          >
            <div className="erp-bar-label">
              {item.icon === undefined ? null : (
                <span style={{ color: item.color ?? BrandColors.blue, display: 'inline-flex' }}>
                  {item.icon}
                </span>
              )}
              <Typography.Text ellipsis style={{ color: BrandColors.inkSecondary }}>
                {item.label}
              </Typography.Text>
            </div>

            <div className="erp-bar-track">
              {item.value > 0 ? (
                <div
                  className="erp-bar"
                  style={{
                    // Chừa chỗ cho con số ở đầu thanh: thanh dài nhất vẫn để con số nằm trọn.
                    width: `calc((100% - 96px) * ${ratio.toFixed(4)})`,
                    background: item.color ?? BrandColors.blue,
                  }}
                />
              ) : null}
              <span className="erp-bar-value">
                {item.value.toLocaleString('vi-VN')}
                {item.extra === undefined ? null : (
                  <span className="erp-bar-extra"> · {item.extra}</span>
                )}
              </span>
            </div>
          </div>
        );

        return item.tooltip === undefined ? (
          <div key={item.key}>{row}</div>
        ) : (
          <Tooltip key={item.key} title={item.tooltip} placement="topLeft" mouseEnterDelay={0.15}>
            {row}
          </Tooltip>
        );
      })}
    </div>
  );
}
